/**
 * VesselSettingsModal - Comprehensive vessel configuration
 * Matches legacy app functionality with all equipment and operational options
 * Uses custom modal wrapper (NOT Catalyst Dialog due to button issues)
 */

import React, { useState, useEffect } from 'react';
import { X, Ship, AlertCircle, Shield, Home, Settings, FileText, Award, ClipboardCheck } from 'lucide-react';
import { useVessel } from '../../../contexts/VesselContext';
import { useCustomDialogs } from '../../../hooks/useCustomDialogs';
import BasicInfoTab from './tabs/BasicInfoTab';
import OperationsTab from './tabs/OperationsTab';
import SafetyEquipmentTab from './tabs/SafetyEquipmentTab';
import VesselSpacesTab from './tabs/VesselSpacesTab';
import SystemsTab from './tabs/SystemsTab';
import RequirementsTab from './tabs/RequirementsTab';
import CertificationTab from './tabs/CertificationTab';
import PreDepartureChecksTab from './tabs/PreDepartureChecksTab';
import { mapConfigToVessel, mapVesselToConfig } from './utils/vesselConfigMapper';

// Import test suite and make it available globally
import { runAllTests } from './tabs/utils/vesselAutoConfig.test';
if (typeof window !== 'undefined') {
  window.testVesselConfig = runAllTests;
  
  // Debug helpers for vessel settings persistence audit
  window.__debugVesselPersistence = () => {
    const vessel = JSON.parse(localStorage.getItem('vessel') || '{}');
    const targetFields = ['gmdssSeaArea', 'mobEquipment', 'lsaEquipment'];
    console.group('🔍 Vessel Settings Persistence Audit');


    targetFields.forEach(field => {
      const value = vessel[field];

    });
    console.groupEnd();
    return { vessel, targetFields };
  };
  
  window.__debugVesselContext = () => {
    // This will need to be called from within a component that has access to useVessel


  };
}

const VesselSettingsModal = ({ isOpen, onClose }) => {
  const { currentVesselId, getCurrentVessel, updateVessel } = useVessel();
  const { showAlert, AlertDialog } = useCustomDialogs();
  const [activeTab, setActiveTab] = useState('basic');
  const [vesselConfig, setVesselConfig] = useState({
    // Basic vessel info
    vesselName: '',
    vesselType: '',
    category: '',
    lengthOverall: '',
    grossTonnage: '',
    draft: '',
    enginePower: '',
    age: '', // Vessel age in years for maintenance decisions
    timezone: '', // FT-269: vessel timezone (IANA name); empty string means "use device default"
    
    // Engine & Propulsion Details
    engineType: 'diesel', // 'diesel', 'petrol', 'outboard'
    enginePosition: 'below_deck', // 'below_deck', 'above_deck'
    numberOfEngines: 1,
    propulsionType: 'shaft', // 'shaft', 'jet', 'outboard'
    steeringType: 'hydraulic', // 'hydraulic', 'mechanical', 'electronic'
    
    // Capacity
    maxPersons: '',
    passengerCapacity: '',
    crewCount: 3,
    
    // Operating status for HOR compliance
    operatingStatus: 'seagoing', // Default to seagoing (more restrictive)
    
    // Inland waterway specific fields (COP13/MSN 1823)
    waterCategory: 'C', // A, B, C, D for inland waters
    carriesVulnerablePassengers: false, // For Category A/B risk assessment
    operatesAtNight: false, // Requires lifejacket lights
    allPersonsWearLifejackets: false, // Exempts lifebuoy requirement per COP13 13.1.4
    hasDecking: true, // Affects fire equipment requirements per COP13 15.1
    hasHeating: false, // Fire risk consideration
    
    // Regulatory path for hire boats (MGN 469)
    inlandRegulatoryPath: 'hire_boat_code', // 'hire_boat_code' or 'class_ix_a'
    
    // Equipment variations (not optional, just type selection)
    lifejacketType: 'foam', // 'foam' or 'inflatable'
    
    // Liferafts - only shown if required by area category
    liferafts_fitted: false,
    liferaft_capacity: '',
    hru_fitted: false,
    hruType: 'disposable', // 'disposable' or 'serviceable'
    
    // Optional boats (not required by regulations)
    lifeboat_fitted: false,
    rescue_boat_fitted: false,
    
    // Accommodation & Spaces
    hasAccommodation: false,
    hasGalley: false,
    galleyHeatSource: 'electric', // Simple field for heat source
    hasWheelhouse: false,
    hasMachinerySpace: false,
    hasEnclosedSpaces: false,
    enclosed_accommodation_present: false,
    cabin_accommodation: false,
    hasGasAppliances: false,
    hasLPGSystem: false,
    
    // Hazards & Risks (from VesselSpacesTab)
    hasLithiumBatteries: false,
    hasConcentratedElectrical: false,
    hasExhaustLeakRisk: false,
    hasGasLeakRisk: false,
    hasFireRiskArea: false,
    gasAppliances: false,
    
    // Systems
    hasEmergencySteering: false,
    power_critical_systems: false,
    bilge_auto: false,
    hasShorepower: false,
    
    // Operations & Equipment Arrays
    operations: [],      // ['dangerous_goods', 'towing_operations', 'survey_operations']
    optionalEquipment: [], // Optional equipment: ['radar', 'gps', 'ais', 'echo_sounder', 'autopilot']
    specialEquipment: [], // ['winch_lifting_gear', 'gas_detection', 'crane', 'dynamic_positioning']
    
    // Environmental
    oilCapacityLitres: '',
    
    // Operations
    operatingHours: 'under_24h', // 'under_24h' or 'over_24h'
    internationalVoyages: false,
    towing_capable: false,
    engineer_onboard: false,
    
    // Equipment Check Settings
    equipmentCheckSettings: {
      lsaFrequency: 30,
      ffeFrequency: 30
    },
    
    // GMDSS and MOB Equipment (critical fields that were missing)
    gmdssSeaArea: '',
    mobEquipment: [],
    
    // LSA Equipment selections (Step 5 wizard fields)
    lsaEquipment: [] // ['immersion_suits', 'thermal_protective_aids', 'line_throwing_apparatus']
  });

  // Load vessel data when modal opens or vessel changes
  useEffect(() => {

    if (currentVesselId && isOpen) {
      // Always get fresh vessel data from context (which is the source of truth)
      const vesselFromContext = getCurrentVessel();

      if (vesselFromContext) {
        // SCHEMA-DRIVEN: Use mapVesselToConfig for ALL fields automatically
        // This ensures Modal loads the same fields as the Wizard - complete round-trip!
        const configFromVessel = mapVesselToConfig(vesselFromContext);


        setVesselConfig(configFromVessel);
      } else {

      }
    }
  }, [currentVesselId, getCurrentVessel, isOpen]); // Update whenever modal opens or vessel changes

  const updateConfig = (updates) => {

    setVesselConfig(prev => {
      const newConfig = {
        ...prev,
        ...updates
      };

      return newConfig;
    });
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleSave = () => {

    // Validate required fields
    if (!vesselConfig.vesselName || !vesselConfig.vesselType || vesselConfig.category === '') {
      showAlert(
        'Required Fields Missing',
        'Please complete all required fields: Name, Type, and Category',
        'warning'
      );
      return;
    }

    // Convert category format if needed (e.g., '4' to 'cat4')
    let categoryForVessel = vesselConfig.category;
    if (categoryForVessel && !categoryForVessel.startsWith('cat')) {
      categoryForVessel = `cat${categoryForVessel}`;
    }


    // Update VesselContext as the primary source of truth
    if (currentVesselId) {
      // SCHEMA-DRIVEN: Use mapConfigToVessel for ALL fields automatically
      // This ensures Modal saves the same fields as the Wizard - no more field loss!
      const vesselUpdates = mapConfigToVessel(vesselConfig);

      // Ensure category uses correct format (mapConfigToVessel handles this, but double-check)
      if (vesselUpdates.category && !vesselUpdates.category.startsWith('cat')) {
        vesselUpdates.category = `cat${vesselUpdates.category}`;
      }


      updateVessel(currentVesselId, vesselUpdates);
      
      // Show success message
      showAlert(
        'Settings Saved',
        'Vessel configuration has been updated successfully.',
        'success'
      );
    }
    
    onClose();
  };

  const tabs = [
    { id: 'basic', label: 'Basic', icon: Ship },
    { id: 'operations', label: 'Operations', icon: AlertCircle },
    { id: 'safety', label: 'Safety', icon: Shield },
    { id: 'spaces', label: 'Spaces', icon: Home },
    { id: 'systems', label: 'Systems', icon: Settings },
    { id: 'predeparture', label: 'Pre-Departure', icon: ClipboardCheck },
    { id: 'certification', label: 'Certification', icon: Award },
    { id: 'requirements', label: 'Requirements', icon: FileText }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />
      
      <div className="relative min-h-screen flex items-center justify-center p-2 sm:p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">Vessel Configuration</h2>
                <p className="text-xs sm:text-sm text-gray-600 mt-1 hidden sm:block">Configure your vessel details for compliance calculations</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto flex-shrink-0">
            <nav className="flex px-4 sm:px-6" style={{ paddingBottom: '0' }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      group inline-flex items-center py-3 px-3 border-b-2 font-medium text-sm whitespace-nowrap relative
                      ${activeTab === tab.id
                        ? 'border-cyan-500 text-cyan-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                    style={{ marginBottom: '-2px' }}
                  >
                    <Icon className={`
                      mr-1.5 h-4 w-4
                      ${activeTab === tab.id ? 'text-cyan-500' : 'text-gray-400 group-hover:text-gray-500'}
                    `} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {activeTab === 'basic' && <BasicInfoTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'operations' && <OperationsTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'safety' && <SafetyEquipmentTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'spaces' && <VesselSpacesTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'systems' && <SystemsTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'predeparture' && <PreDepartureChecksTab />}
            {activeTab === 'certification' && <CertificationTab vesselConfig={vesselConfig} updateConfig={updateConfig} />}
            {activeTab === 'requirements' && <RequirementsTab vesselConfig={vesselConfig} />}

            {/* Actions - inside content to ensure visibility */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-xs text-gray-500">
                    * Required fields
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      // Reset to empty config
                      setVesselConfig({
                        vesselName: '',
                        vesselType: '',
                        category: '',
                        lengthOverall: '',
                        grossTonnage: '',
                        draft: '',
                        enginePower: '',
                        age: '',
                        engineType: 'diesel',
                        enginePosition: 'below_deck',
                        numberOfEngines: 1,
                        propulsionType: 'shaft',
                        steeringType: 'hydraulic',
                        maxPersons: '',
                        passengerCapacity: '',
                        crewCount: 3,
                        operatingStatus: 'seagoing',
                        operatingHours: 'under_24h',
                        lifejacketType: 'foam',
                        hasLiferafts: false,
                        liferaftCapacity: '',
                        hasEpirb: false,
                        hasSart: false,
                        hasGmdss: false,
                        optionalEquipment: [],
                        hasAccommodation: false,
                        hasGalley: false,
                        hasToilets: false,
                        hasEngineRoom: false,
                        hasCargoHold: false,
                        hasWorkshop: false,
                        hasEmergencySteering: false,
                        bilge_auto: false,
                        power_critical: false,
                        engineers_onboard: false,
                        companyRequiredDrills: [],
                        specialEquipment: [],
                        autoRequiredEquipment: [],
                        autoRequiredFireSystem: false,
                        operations: []
                      });
                      setActiveTab('basic');
                    }}
                    className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 focus:outline-none"
                  >
                    🔄 Reset All Fields
                  </button>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-white bg-cyan-600 rounded-md hover:bg-cyan-700"
                    onClick={handleSave}
                  >
                    Save Configuration
                  </button>
                </div>
              </div>
            </div>

            {/* Extra padding to ensure scrollability */}
            <div className="h-20"></div>
          </div>
        </div>
      </div>
      
      {/* Custom Dialogs */}
      <AlertDialog />
    </div>
  );
};

export default VesselSettingsModal;